import React from 'react';

// Components
import { Button } from '@capintel/component-lib/button';
import { Filter } from '@/views/components/inputs';

// Types
import { ComparisonItemsFilter } from '@/redux/modules/types/comparison';
import { DropdownGroupSelection } from '@/views/components/inputs/FilterDropdown';
import ComparisonItem from '@/types/comparison-item';
import DropdownOptionsGroup from '@/types/dropdown-options-group';
import AppMode from '@/types/app-mode';
import User from '@/types/user';
import DropdownItem from '@/types/dropdown-item';

// Utils
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { formatMessage, FormattedMessage, formatLocalizedString } from '@/utils/i18n';

export type Props = {
  readOnly?: boolean;
  items: ComparisonItem[];
  filter: string[];
  onChangeFilter: (filter: string[]) => void;
  onAddGroup: () => void;
  onManageAccountSets: () => void;
  showGroups?: boolean;
  showAccountSets?: boolean;
  user: User;
};

const ComparisonTableOptions: React.FC<Props> = ({
  readOnly,
  items,
  filter,
  onChangeFilter,
  onAddGroup,
  onManageAccountSets,
  showGroups,
  showAccountSets,
  user,
}) => {
  const getFilterOptionsGroups = (): DropdownOptionsGroup[] => {
    const groups: DropdownOptionsGroup[] = [];

    const hasGroups = items.some(item => item.type === COMPARISON_ITEM_TYPES.GROUP);
    if (hasGroups) {
      groups.push({
        label: formatMessage('Comparison.ItemFilter.Categories.Type'),
        selection: DropdownGroupSelection.Radio,
        options: [
          {
            value: ComparisonItemsFilter.Products,
            label: formatMessage('Comparison.ItemFilter.Options.Products'),
          },
          {
            value: ComparisonItemsFilter.Groups,
            label: formatMessage('Comparison.ItemFilter.Options.Groups'),
          },
        ],
      });
    }

    groups.push({
      label: formatMessage('Comparison.ItemFilter.Categories.Items'),
      selection: DropdownGroupSelection.Checkbox,
      options: items.map(item => ({
        value: item._id,
        label: formatLocalizedString(item.name),
        type: item.type,
        color: item.color,
      })),
    });

    return groups;
  };

  const renderAddGroupButton = () => (
    <Button variant="secondary" appearance="text" size="small" icon="add" onClick={onAddGroup} data-testid="comparisonTable-secondaryButton-addGroup">
      <FormattedMessage id="Comparison.Groups.Tabs.AddGroup" />
    </Button>
  );

  const renderAccountSetsButton = () => (
    <Button
      variant="secondary"
      appearance="text"
      size="small"
      icon="account-tree"
      onClick={onManageAccountSets}
      data-testid="comparisonTable-secondaryButton-manageAccountSets"
    >
      <FormattedMessage id="Comparison.AccountSets.ButtonText" />
    </Button>
  );

  const optionsGroups = getFilterOptionsGroups();

  const value = optionsGroups.reduce((acc, group) => acc.concat(group.options.filter(option => filter.includes(option.value))), [] as DropdownItem[]);

  const canShowActions = !readOnly && user.appConfig.mode !== AppMode.LINK;
  const shouldShowAccountSets = canShowActions && showAccountSets;
  const shouldShowGroups = canShowActions && showGroups;

  return (
    <div className="comparison-table-options">
      <Filter
        data-private
        data-dd-privacy="mask"
        label={formatMessage('Comparison.ItemFilter.FilterLabel')}
        onChange={value => onChangeFilter(value.map(val => val.value))}
        className="items-filter"
        optionsGroups={optionsGroups}
        search
        multiselect
        value={value}
      />

      {canShowActions && (
        <div className="options-actions">
          {shouldShowAccountSets && renderAccountSetsButton()}
          {shouldShowGroups && renderAddGroupButton()}
        </div>
      )}
    </div>
  );
};

export default ComparisonTableOptions;
